import React, { useEffect } from "react";
import { Form } from "antd";
import formStyles from "components/FormItems/formItems.module.scss";
import TextareaAutosize from "react-textarea-autosize";
import { replaceUnicodePunctuation } from "common/utils/unicodePunctuation";

function MessageBox(props) {
  const {
    label,
    placeholder = "Type a message",
    name,
    value,
    onChange,
    onShortUrlRemoved,
    onPaste,
    error,
    touched,
    disabled,
    secondary = false,
    minRows = 8,
    maxRows = 6,
    containerClassname = "",
    textAreaClassname = "",
    containerProps = {},
    textAreaProps = {},
    colon = false,
    textAreaRef,
    style,
    shortUrlMarkers = []
  } = props;

  let finalClassNames = formStyles.formInputPrimary;
  if (secondary) {
    finalClassNames = formStyles.formInputSecondary;
  }

  function scrollToView() {
    if (textAreaProps.ref) {
      textAreaProps.ref.current.scrollTop = textAreaProps?.ref.current.scrollHeight;
    }
  }

  useEffect(() => {
    scrollToView();
  }, [value]);

  const markers = shortUrlMarkers && shortUrlMarkers.length ? shortUrlMarkers : [process.env.REACT_SHORT_URL_SAMPLE].filter(Boolean);
  const markerRegexes = markers.map(pattern => {
    const escaped = String(pattern).replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
    return new RegExp(escaped, "gi");
  });
  const mergeFieldRegex = /{{[^}]+}}/g;

  const handleKeyDown = e => {
    const target = e.target;
    const currentValue = target.value;
    const cursorPos = target.selectionStart;

    if (e.ctrlKey || e.metaKey || e.altKey || ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Shift", "Tab"].includes(e.key)) {
      return;
    }

    if (cursorPos === null || cursorPos === undefined) return;

    const removeMatchedToken = matches => {
      for (let match of matches) {
        const markerStart = match.index;
        const end = markerStart + match[0].length;

        const isCursorInside = cursorPos > markerStart && cursorPos < end;
        const isBackspaceAtEnd = e.key === "Backspace" && cursorPos === end;

        if (isCursorInside || isBackspaceAtEnd) {
          e.preventDefault();

          const newValue = currentValue.slice(0, markerStart) + currentValue.slice(end);
          target.value = newValue;
          target.setSelectionRange(markerStart, markerStart);

          if (typeof onShortUrlRemoved === "function") {
            onShortUrlRemoved();
          }

          if (typeof onChange === "function") {
            onChange({ target: { name, value: newValue } });
          }
          return true;
        }
      }
      return false;
    };

    for (const markerRegex of markerRegexes) {
      const shortUrlMatches = [...currentValue.matchAll(new RegExp(markerRegex.source, markerRegex.flags))];
      if (removeMatchedToken(shortUrlMatches)) return;
    }

    const mergeFieldMatches = [...currentValue.matchAll(mergeFieldRegex)];
    removeMatchedToken(mergeFieldMatches);
  };

  return (
    <Form.Item label={label} validateStatus={error && touched ? "error" : ""} help={touched ? error : ""} className={`${finalClassNames} ${containerClassname}`} colon={colon} {...containerProps}>
      <TextareaAutosize
        name={name}
        placeholder={placeholder}
        value={value ? value : ""}
        onChange={e => {
          try {
            const nextValue = replaceUnicodePunctuation(e.target.value).replace(/\u2019/g, "'");
            if (nextValue !== e.target.value) {
              const { selectionStart, selectionEnd } = e.target;
              e.target.value = nextValue;
              if (typeof selectionStart === "number" && typeof selectionEnd === "number") {
                e.target.setSelectionRange(selectionStart, selectionEnd);
              }
            }
            onChange(e);
          } catch (error) {
            onChange(e);
          }
        }}
        minRows={minRows}
        maxRows={maxRows}
        disabled={disabled}
        onKeyDown={handleKeyDown}
        onPaste={e => {
          if (onPaste) onPaste(e);
        }}
        className={`${textAreaClassname} py-0 border-0`}
        {...textAreaProps}
        ref={textAreaRef}
        style={{ lineHeight: "25px", resize: "none", ...style }}
      />
    </Form.Item>
  );
}

export default MessageBox;
